<template>
  <div v-for="category in categories" :key="category.category" class="mb-8 last:mb-0">
    <h3 class="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4 border-b pb-2">
      {{ t('categories.' + category.category) }}
    </h3>
    <div class="overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-100">
        <tbody class="divide-y divide-gray-100">
          <tr v-for="f in category.fields" :key="f.path" class="group">
            <th scope="row" class="py-4 pr-4 text-left text-sm font-medium text-gray-500 w-1/3 align-top">
              {{ t('fields.' + f.path) }}
            </th>
            <td class="py-4 pl-4 text-sm text-gray-900 align-top">
              <template v-if="f.path && Array.isArray(get(f.path)) && f.path in filters">
                <div v-for="(s, i) in filters[f.path](get(f.path)).filter(v => v)" :key="i">
                  {{ s }}
                </div>
              </template>
              <template v-else>
                <span class="whitespace-pre-line">{{ getContent(f) }}</span>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePageContext } from "vike-vue/usePageContext";
import { useAPI, useCurrency, useAreaUnits } from '@repo/ui';
import type { Deal, Field } from '@repo/types'

interface Station {
  name: string;
  minutes?: number;
  walkTime?: number;
}

const props = defineProps<{
  deal: Deal;
  currencies: Record<string, number>;
}>();

const { t, locale } = useI18n();
const api = useAPI();
const pageContext = usePageContext();
const { format: formatPrice } = useCurrency(props.currencies);
const { format: formatArea } = useAreaUnits();

const fields = ref<Field[]>(pageContext.data.fields ?? []);
const loading = ref(false);

const categoriesConfig = [
    {category: 'basic', label: '基本情報'},
    {category: 'price', label: '金額'},
    {category: 'rent', label: '賃料'},
    {category: 'lease', label: '賃貸'},
    {category: 'area', label: '広さ'},
    {category: 'access', label: 'アクセス'},
    {category: 'building', label: '建物情報'},
    {category: 'land', label: '土地情報'},
    {category: 'parking', label: '駐車場'},
    {category: 'cityPlan', label: '法規'},
    {category: 'investment', label: '収益物件'},
    {category: 'handover', label: '引渡'},
];

const categories = computed(() => {
  return categoriesConfig
    .map(category => {
      const categoryFields = fields.value
        .filter(field => field.category === category.category);
      return { ...category, fields: categoryFields };
    })
    .filter(({ fields }) => fields.length);
});

const get = (path: string): unknown => {
  const getSqm = () => {
    if (props.deal.building?.type === 'land') return props.deal.land?.sqm;
    return props.deal.sqm || props.deal.building?.sqm;
  };

  if (path === 'computed.structure') {
    const building = props.deal.building ?? {};
    return [building.structure, building.floorsCount ? `${building.floorsCount}${t('units.floor')}` : null].filter(s => s).join(' / ');
  }
  if (path === 'computed.monthlyCost') {
    return (props.deal.repairReserveFund || 0) + (props.deal.managementFee || 0);
  }
  if (path === 'computed.floor') {
    return [props.deal.floor, props.deal.building?.floorsCount ? `${props.deal.building.floorsCount}${t('units.floor')}` : null].filter(n => n).join(' / ');
  }
  if (path === 'computed.nearestStation') {
    return props.deal.building?.nearestStations?.slice(0, 1);
  }
  if (path === 'tsubo') return (getSqm() || 0) / 3.305785;
  if (path === 'building.tsubo') return (props.deal.building?.sqm || 0) / 3.305785;
  if (path === 'land.tsubo') return (props.deal.land?.sqm || 0) / 3.305785;
  if (path === 'pricePerTsubo') {
    const sqm = getSqm();
    return props.deal.price && sqm ? (props.deal.price / sqm) * 3.305785 : null;
  }
  if (path === 'rentPerTsubo') {
    const sqm = getSqm();
    return props.deal.rent && sqm ? (props.deal.rent / sqm) * 3.305785 : null;
  }

  return path.split('.').reduce((obj: object, key: string) => obj?.[key], props.deal);
};

const getWithFallback = (field: Field) => {
  if (field.fallback) return get(field.path) ?? get(field.fallback);
  return get(field.path);
};

const formatStation = (s: Station) => {
  const walkTime = s.minutes ?? s.walkTime;
  const walkTimeString = walkTime ? t('property.min_walk', { min: walkTime }) : '';
  return `${s.name}${walkTimeString ? ` (${walkTimeString})` : ''}`;
};

const filters: Record<string, (val: unknown[]) => string[]> = {
  'building.nearestStations': (stations: unknown[]) => {
    return (stations as Station[]).map(formatStation);
  },
  'computed.nearestStation': (stations: unknown[]) => {
    return (stations as Station[]).map(formatStation);
  }
};

const getContent = (f: Field) => {
  const value = getWithFallback(f);
  if (value === null || value === undefined || value === '') return '-';

  const isJapanese = locale.value.startsWith('ja');

  const getValue = (val: unknown, field: Field): string => {
    // Boolean handling
    if (field.type === 'boolean') {
      return val ? t('values.true') : t('values.false');
    }

    // Date handling
    if (field.type === 'date' && (typeof val === 'string' || typeof val === 'number' || val instanceof Date)) {
      try {
        const date = new Date(val);
        return date.toLocaleDateString(locale.value, { year: 'numeric', month: 'long' });
      } catch {
        return String(val);
      }
    }

    // Price handling
    if (field.unit?.startsWith?.('yen') && typeof val === 'number') {
      return formatPrice.value(val);
    }

    // Area handling
    if (field.unit === 'sqm' && typeof val === 'number') {
      return formatArea.value(val);
    }

    if (field.unit === 'tsubo' && typeof val === 'number') {
      return new Intl.NumberFormat(locale.value, { maximumFractionDigits: 2 }).format(val) + t('units.tsubo');
    }

    // Enum handling (if needed)
    if (field.type === 'enum' || field.path.includes('Status') || field.path.includes('Type')) {
        // Try to translate the value
        const translationKey = `values.${val}`;
        const translated = t(translationKey);
        if (translated !== translationKey) return translated;
    }

    let result = val;

    function isValueObject(val: unknown): val is { value: unknown, unit: string } {
      return typeof val === 'object' && val !== null && 'value' in val && 'unit' in val
    }
    if (isValueObject(val)) {
       return getValue(val.value, { ...field, ...val });
    }

    // Add units if available and not already handled
    if (field.unit && field.unit !== 'none') {
        const unitLabel = t('units.' + field.unit);
        if (unitLabel && unitLabel !== 'units.' + field.unit) {
            result = isJapanese ? `${result}${unitLabel}` : `${result} ${unitLabel}`;
        }
    }

    return String(result);
  };

  return getValue(value, f);
};

const loadFields = async () => {
  if (!props.deal?.type || !props.deal?.building?.type) return;
  loading.value = true;
  try {
    const response = await api.call('fields', props.deal.type, props.deal.building.type);
    fields.value = response.data.fields || [];
  } catch (error) {
    console.error('Error loading fields:', error);
  } finally {
    loading.value = false;
  }
};

watch(() => [props.deal?.type, props.deal?.building?.type], loadFields);

</script>
